import { StateCreator } from 'zustand';
import { DateOrFalse, Magazine, pageFiles } from '../../Datatypes';

export interface StoredMagazineZustandSlice {
  storedMagazines: Magazine[];

  updateStoredMagazine: (newMagazine: Magazine) => void;
  getStoredMagazineByMagazine: (magazine: Magazine) => Magazine | null;
  getStoredMagazineByDate: (date: string) => Magazine[];
  getAllStoredEpapers: () => Magazine[];
  deleteStoredMagazineByMagazine: (magazine: Magazine) => void;
  deleteAllStoredMagazines: () => void;
  updatePageFile: (magazine: Magazine, pageNumber: number, value: DateOrFalse, size: number) => void;
  getPageFileByMagazineAndPageNumber: (magazine: Magazine | null, pageNumber: number) => pageFiles | null;

  _hasHydrated: boolean;
  setHasHydrated: (state: any) => void;
}

export const createStoredMagazineZustandSlice: StateCreator<StoredMagazineZustandSlice> = (set, get) => ({
  storedMagazines: [],

  updateStoredMagazine: (newMagazine) => {
    if (!newMagazine) {
      console.warn('Invalid magazine object for update:', newMagazine);
      return;
    }
    set((state) => {
      const existingIndex = state.storedMagazines.findIndex(
        (stored) =>
          stored?.date === newMagazine.date &&
          stored?.edition === newMagazine.edition
      );
      
      if (existingIndex !== -1) {
        const updatedMagazines = [...state.storedMagazines];
        updatedMagazines[existingIndex] = newMagazine;
        //console.log("updateStoredMagazine - magazine updated");
        return { storedMagazines: updatedMagazines };
      } else {
        //console.log("updateStoredMagazine - magazine added");
        return { storedMagazines: [...state.storedMagazines, newMagazine] };
      }      
    });    
  },

  getStoredMagazineByMagazine: (magazine) => {
    if (!magazine || !magazine.date || !magazine.edition) {
      console.warn('Invalid magazine object:', magazine);
      return null;
    }
    //console.log("StoredMagazineZusatandSlice-magazine.date, magazine.edition:" , magazine.date, magazine.edition);
    const storedMagazines = get().storedMagazines;

    return (
      storedMagazines.find((stored) => {
        if (!stored) {
          console.warn('Malformed stored magazine object:', stored);
          return false;
        }
        return (
          stored.date === magazine.date &&
          stored.edition === magazine.edition
        );
      }) || null
    );
  },

  getStoredMagazineByDate: (date) => {
    if (!date) {
      console.warn('Invalid date provided for fetching stored magazines:', date);
      return [];
    }

    return get().storedMagazines.filter(
      (stored) => stored.date === date
    );
  },

  getAllStoredEpapers: () => {
    return get().storedMagazines;
  },

  deleteStoredMagazineByMagazine: (magazine) => {
    set((state) => {
      const foundMagazines = state.storedMagazines.filter(
        (stored) => stored?.date === magazine.date && stored?.edition === magazine.edition
      );

      if (foundMagazines.length > 0) {
        //console.log("Magazines found and deleted:", foundMagazines);
        const updatedMagazines = state.storedMagazines.filter(
          (stored) => !(stored?.date === magazine.date && stored?.edition === magazine.edition)
        );
        return { storedMagazines: updatedMagazines };
  
      } else {
        console.log("No magazines found for deletion.");
        return { storedMagazines: foundMagazines };
      }
    });
  },

  deleteAllStoredMagazines: () => {
    set(() => ({
      storedMagazines: [], // Clear all stored magazines
    }));
    console.log("All stored magazines have been deleted.");
  },

  updatePageFile: (magazine: Magazine, pageNumber: number, value: DateOrFalse, size: number) => {
    set((state) => {
      const stored = state.storedMagazines.find(
        (stored) =>
          stored.date === magazine.date &&
          stored.edition === magazine.edition
      );

      if (!stored || !stored.pageFiles) {
        //console.warn('updatePageFile - No stored magazine found for the given magazine:', magazine);
        return state;
      }

      const updatedPageFiles = stored.pageFiles.map((pageFile) => {
        if (pageFile.page === pageNumber) {
          return ({ ...pageFile, downloaded: value, filesize: size });
        }
        return pageFile;
      });

      const updatedMagazines = state.storedMagazines.map((storedItem) =>
        storedItem === stored ? { ...stored, pageFiles: updatedPageFiles } : storedItem
      );

      return { storedMagazines: updatedMagazines }

    });
  },

  getPageFileByMagazineAndPageNumber: (magazine: Magazine | null, pageNumber: number) => {
    if (!magazine) {
      //console.warn('Invalid magazine object:', magazine);
      return null;
    }

    const storedMagazines = get().storedMagazines;

    // Find the magazine in stored magazines
    const foundMagazine = storedMagazines.find(
      (stored) =>
        stored.date === magazine.date &&
        stored.edition === magazine.edition
    );
    if (!foundMagazine) {
      //console.log('Magazine not found:', magazine);
      return null;
    }
    // Find the pageFile for the specified pageNumber in the magazine
    const foundPageFile = foundMagazine.pageFiles?.find(
      (pageFile) => Number(pageFile.page) === pageNumber
    );
    if (!foundPageFile) {
      //console.warn('Page number not found in the specified magazine:', pageNumber);
      return null;
    }

    return foundPageFile;
  },

  _hasHydrated: false,
  setHasHydrated: (state: any) => {
    set({ _hasHydrated: state });
  },
});